import * as THREE from 'three';

/**
 * Overlay materials are transparent (at full opacity) purely so a bird high above the fog veil can be sorted after it:
 * the veil is a transparent layer too, and opaque things always draw before it.
 */
export const OVERLAY_ORDER = 101;

const materials = new Map<string, THREE.MeshStandardMaterial>();
export function paint(color: number, overlay = false) {
  const key = `${color}:${overlay}`;
  let material = materials.get(key);
  if (!material) {
    material = new THREE.MeshStandardMaterial({ color, roughness: .92, flatShading: true, side: THREE.DoubleSide, transparent: overlay });
    materials.set(key, material);
  }
  return material;
}

/** A tapered flat panel lying in the XZ plane, `length` outward along +X, swept back toward -Z. */
function panel(length: number, rootChord: number, tipChord: number, sweep: number) {
  const shape = new THREE.Shape();
  shape.moveTo(0, rootChord * .35); shape.lineTo(length, rootChord * .35 - sweep);
  shape.lineTo(length, rootChord * .35 - sweep - tipChord); shape.lineTo(0, -rootChord * .65); shape.closePath();
  const geometry = new THREE.ShapeGeometry(shape); geometry.rotateX(Math.PI / 2); return geometry;
}

let unitSphere: THREE.SphereGeometry | undefined;
const cones = new Map<string, THREE.ConeGeometry>(), panels = new Map<string, THREE.BufferGeometry>(), cylinders = new Map<string, THREE.CylinderGeometry>();

/** Everything a figure is built from is cached, so a hundred spawns share a handful of buffers. */
export const parts = {
  /** A low-poly ellipsoid centred on `(x, y, z)`. */
  blob(parent: THREE.Object3D, color: number, radii: [number, number, number], at: [number, number, number], overlay = false) {
    unitSphere ??= new THREE.SphereGeometry(1, 8, 6);
    const mesh = new THREE.Mesh(unitSphere, paint(color, overlay));
    mesh.scale.set(...radii); mesh.position.set(...at); mesh.castShadow = true; parent.add(mesh); return mesh;
  },
  cone(parent: THREE.Object3D, color: number, radius: number, length: number, overlay = false) {
    const key = `${radius}:${length}`; let geometry = cones.get(key);
    if (!geometry) { geometry = new THREE.ConeGeometry(radius, length, 5); cones.set(key, geometry); }
    const mesh = new THREE.Mesh(geometry, paint(color, overlay)); mesh.castShadow = true; parent.add(mesh); return mesh;
  },
  stick(parent: THREE.Object3D, color: number, radius: number, length: number, overlay = false) {
    const key = `${radius}:${length}`; let geometry = cylinders.get(key);
    if (!geometry) { geometry = new THREE.CylinderGeometry(radius, radius, length, 4); cylinders.set(key, geometry); }
    const mesh = new THREE.Mesh(geometry, paint(color, overlay)); mesh.castShadow = true; parent.add(mesh); return mesh;
  },
  /** A flat, tapered feather panel lying in the XZ plane.  */
  feathers(parent: THREE.Object3D, color: number, length: number, rootChord: number, tipChord: number, sweep: number, overlay = false) {
    const key = [length, rootChord, tipChord, sweep].join(':'); let geometry = panels.get(key);
    if (!geometry) { geometry = panel(length, rootChord, tipChord, sweep); panels.set(key, geometry); }
    const mesh = new THREE.Mesh(geometry, paint(color, overlay)); mesh.castShadow = true; parent.add(mesh); return mesh;
  },
};

const skins = new Map<boolean, THREE.MeshStandardMaterial>();
/** The painted look of `paint`, with each part's colour carried by its vertices. */
function skinMaterial(overlay: boolean) {
  let material = skins.get(overlay);
  if (!material) skins.set(overlay, material = new THREE.MeshStandardMaterial({ vertexColors: true, roughness: .92, flatShading: true, side: THREE.DoubleSide, transparent: overlay }));
  return material;
}
/** Shadows of critter skins, kept off three's shared depth material so rigid and skinned shadow draws do not keep swapping its program. */
let skinDepth: THREE.MeshDepthMaterial | undefined;

/**
 * Draws a finished critter as one skinned mesh instead of a mesh per part: a gull is about thirty parts, so the camp's flocks and
 * crabs were around a thousand draws a frame, shadows included. Every part becomes a bone of its own, carried by the same groups
 * as before, so `apply` animates exactly what it did; its geometry is copied in the part's own space with the part's colour,
 * and the part itself stops drawing. Call at the end of the model's constructor, once every part exists. Flat shading takes its
 * normals from the drawn surface, so a stretched wing or leg shades as it did.
 */
export function bakeCritter(root: THREE.Object3D) {
  const groups = new Map<boolean, THREE.Mesh[]>();
  root.traverse(object => {
    const mesh = object as THREE.Mesh;
    if (!mesh.isMesh) return;
    const overlay = (mesh.material as THREE.Material).transparent;
    let list = groups.get(overlay); if (!list) groups.set(overlay, list = []); list.push(mesh);
  });
  root.updateMatrixWorld(true);
  const toRoot = root.matrixWorld.clone().invert(), local = new THREE.Matrix4(), point = new THREE.Vector3(), color = new THREE.Color();
  for (const [overlay, parts] of groups) {
    let vertices = 0, indices = 0;
    for (const { geometry } of parts) { vertices += geometry.attributes.position.count; indices += geometry.index?.count ?? geometry.attributes.position.count; }
    const positions = new Float32Array(vertices * 3), colors = new Float32Array(vertices * 3), skinIndex = new Uint16Array(vertices * 4), skinWeight = new Float32Array(vertices * 4);
    const index = new Uint32Array(indices), bounds = new THREE.Box3();
    let v = 0, i = 0;
    parts.forEach((part, bone) => {
      const { geometry } = part, source = geometry.attributes.position, base = v;
      color.copy((part.material as THREE.MeshStandardMaterial).color);
      local.multiplyMatrices(toRoot, part.matrixWorld);
      for (let k = 0; k < source.count; k++, v++) {
        point.fromBufferAttribute(source, k);
        positions[v * 3] = point.x; positions[v * 3 + 1] = point.y; positions[v * 3 + 2] = point.z;
        bounds.expandByPoint(point.applyMatrix4(local));
        colors[v * 3] = color.r; colors[v * 3 + 1] = color.g; colors[v * 3 + 2] = color.b;
        skinIndex[v * 4] = bone; skinWeight[v * 4] = 1;
      }
      const count = geometry.index?.count ?? source.count;
      for (let k = 0; k < count; k++) index[i++] = base + (geometry.index ? geometry.index.getX(k) : k);
      part.visible = false;
    });
    const geometry = new THREE.BufferGeometry();
    geometry.setAttribute('position', new THREE.BufferAttribute(positions, 3)); geometry.setAttribute('color', new THREE.BufferAttribute(colors, 3));
    geometry.setAttribute('skinIndex', new THREE.BufferAttribute(skinIndex, 4)); geometry.setAttribute('skinWeight', new THREE.BufferAttribute(skinWeight, 4));
    geometry.setIndex(new THREE.BufferAttribute(index, 1));
    const skin = new THREE.SkinnedMesh(geometry, skinMaterial(overlay));
    // Bone inverses and the bind matrix stay identity: a vertex goes through its part's world matrix and nothing else.
    skin.bind(new THREE.Skeleton(parts as unknown as THREE.Bone[], parts.map(() => new THREE.Matrix4())), new THREE.Matrix4());
    skin.name = 'critter-skin'; skin.castShadow = parts.some(part => part.castShadow); skin.receiveShadow = parts.some(part => part.receiveShadow);
    skin.renderOrder = parts[0].renderOrder; skin.customDepthMaterial = skinDepth ??= new THREE.MeshDepthMaterial();
    // Spread wings and strides reach past the resting pose; the sphere only has to hold the critter for culling.
    const sphere = bounds.getBoundingSphere(new THREE.Sphere()); sphere.radius *= 1.6; skin.boundingSphere = sphere;
    root.add(skin);
  }
}

/** Takes a critter out of the world and frees what `bakeCritter` made for it alone (its skin and bone texture); parts and materials are shared. */
export function releaseCritter(root: THREE.Object3D) {
  root.removeFromParent();
  root.traverse(object => { if (object instanceof THREE.SkinnedMesh && object.name === 'critter-skin') { object.geometry.dispose(); object.skeleton.dispose(); } });
}

/** Which draw layer a figure's meshes use: 0 with the world, or after the fog veil. */
export function setDrawLayer(root: THREE.Object3D, order: number) {
  root.traverse(object => { if (object instanceof THREE.Mesh) object.renderOrder = order; });
}
